import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ShippingSummary } from '../../types';

interface ShippingSummaryCardProps {
  summary?: ShippingSummary;
  isLoading?: boolean;
}

export function ShippingSummaryCard({ summary, isLoading }: ShippingSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Shipping Overview</CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to="/wms/shipping">View All</Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {summary.pending_shipments}
            </div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {summary.shipped_today}
            </div>
            <div className="text-sm text-muted-foreground">Shipped Today</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {summary.shipped_this_week}
            </div>
            <div className="text-sm text-muted-foreground">This Week</div>
          </div>
        </div>

        {summary.by_carrier && summary.by_carrier.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-sm font-medium mb-2">By Carrier (Last 7 Days)</div>
            <div className="space-y-1">
              {summary.by_carrier.slice(0, 4).map((carrier) => (
                <div key={carrier.carrier} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{carrier.carrier || 'Unknown'}</span>
                  <span className="font-medium">{carrier.count} shipment(s)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {summary.recent_shipments && summary.recent_shipments.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-sm font-medium mb-2">Recent Shipments</div>
            <div className="space-y-2">
              {summary.recent_shipments.slice(0, 3).map((shipment) => (
                <Link
                  key={shipment.id}
                  to={`/wms/shipping/${shipment.id}`}
                  className="block p-2 rounded hover:bg-muted/50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-mono text-sm">{shipment.slip_number}</span>
                      {shipment.carrier && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          via {shipment.carrier}
                        </span>
                      )}
                    </div>
                    {shipment.tracking_number && (
                      <span className="text-xs font-mono text-muted-foreground">
                        {shipment.tracking_number}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
